/**
 * AdminAdsPage - Gestion complète des publicités
 * Onglets: Campagnes, Tarifs, Devis, Factures
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Edit2, Trash2, Eye, EyeOff, BarChart3, 
  ExternalLink, MousePointer, Image, Palette, 
  Calendar, Link2, Type, RefreshCw, X, Check,
  TrendingUp, Target, Megaphone, MapPin, Euro,
  FileText, Receipt, Send, CheckCircle, Clock,
  Building2, Mail, Phone, User, Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';
const API = `${API_URL}/api`;

// ══════════════════════════════════════════════════════════════════════════════
// CONSTANTES
// ══════════════════════════════════════════════════════════════════════════════
const FRENCH_REGIONS = [
  "Auvergne-Rhône-Alpes", "Bourgogne-Franche-Comté", "Bretagne",
  "Centre-Val de Loire", "Corse", "Grand Est", "Hauts-de-France",
  "Île-de-France", "Normandie", "Nouvelle-Aquitaine", "Occitanie",
  "Pays de la Loire", "Provence-Alpes-Côte d'Azur"
];

const DURATION_LABELS = {
  week: "Semaine", month: "Mois", quarter: "Trimestre", year: "Année"
};

const PAGE_LABELS = {
  marketplace: "Marketplace", artisans: "Artisans", reseau: "Réseau"
};

const STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-700",
  sent: "bg-blue-100 text-blue-700",
  accepted: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  expired: "bg-yellow-100 text-yellow-700",
  paid: "bg-emerald-100 text-emerald-700",
  overdue: "bg-orange-100 text-orange-700",
  cancelled: "bg-gray-200 text-gray-600",
};

const STATUS_LABELS = {
  draft: "Brouillon", sent: "Envoyé", accepted: "Accepté",
  rejected: "Refusé", expired: "Expiré", paid: "Payée",
  overdue: "En retard", cancelled: "Annulée"
};

// ══════════════════════════════════════════════════════════════════════════════
// MODAL CAMPAGNE
// ══════════════════════════════════════════════════════════════════════════════
const AdModal = ({ ad, onClose, onSave }) => {
  const isEdit = !!ad?.id;
  const [formData, setFormData] = useState({
    name: ad?.name || '',
    tagline: ad?.tagline || '',
    description: ad?.description || '',
    url: ad?.url || '',
    cta: ad?.cta || 'Découvrir',
    images: { desktop: ad?.images?.desktop || '', mobile: ad?.images?.mobile || '' },
    colors: {
      primary: ad?.colors?.primary || '#1E3A5F',
      secondary: ad?.colors?.secondary || '#F97316',
      gradient: ad?.colors?.gradient || 'linear-gradient(135deg, #1E3A5F 0%, #2d5a8a 50%, #1E3A5F 100%)',
      text: ad?.colors?.text || '#ffffff',
    },
    animation: ad?.animation || 'glow',
    active: ad?.active ?? true,
    priority: ad?.priority || 1,
    pages: ad?.pages || ['marketplace', 'artisans', 'reseau'],
    geolocation: ad?.geolocation || { target_all: true, regions: [], departments: [], cities: [] },
    advertiser_name: ad?.advertiser_name || '',
    advertiser_email: ad?.advertiser_email || '',
    advertiser_phone: ad?.advertiser_phone || '',
  });
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('info');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const url = isEdit ? `${API}/ads/admin/${ad.id}` : `${API}/ads/admin/create`;
      await axios[isEdit ? 'put' : 'post'](url, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(isEdit ? 'Campagne mise à jour' : 'Campagne créée');
      onSave();
      onClose();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { id: 'info', label: 'Informations', icon: Type },
    { id: 'media', label: 'Visuels', icon: Image },
    { id: 'style', label: 'Style', icon: Palette },
    { id: 'geo', label: 'Ciblage', icon: MapPin },
    { id: 'contact', label: 'Annonceur', icon: User },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit ? 'Modifier la campagne' : 'Nouvelle campagne'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
        </div>

        {/* Section Tabs */}
        <div className="flex border-b px-4">
          {sections.map(sec => (
            <button
              key={sec.id}
              onClick={() => setActiveSection(sec.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeSection === sec.id 
                  ? 'border-orange-500 text-orange-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <sec.icon size={16} />
              {sec.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {/* SECTION: Informations */}
          {activeSection === 'info' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input type="text" required value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="Ex: Montée Pilote" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slogan *</label>
                  <input type="text" required value={formData.tagline}
                    onChange={(e) => setFormData({...formData, tagline: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="Ex: Décolle vers ton rêve" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input type="text" value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="Description courte" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
                  <input type="url" required value={formData.url}
                    onChange={(e) => setFormData({...formData, url: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="https://..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Texte bouton</label>
                  <input type="text" value={formData.cta}
                    onChange={(e) => setFormData({...formData, cta: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                  <input type="number" min="1" max="100" value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <button type="button" onClick={() => setFormData({...formData, active: !formData.active})}
                    className={`w-full px-3 py-2 rounded-lg font-medium ${
                      formData.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                    {formData.active ? '✓ Active' : '○ Inactive'}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pages</label>
                <div className="flex gap-2">
                  {Object.entries(PAGE_LABELS).map(([id, label]) => (
                    <button key={id} type="button"
                      onClick={() => {
                        const pages = formData.pages.includes(id)
                          ? formData.pages.filter(p => p !== id)
                          : [...formData.pages, id];
                        setFormData({...formData, pages});
                      }}
                      className={`px-4 py-2 rounded-lg font-medium ${
                        formData.pages.includes(id)
                          ? 'bg-orange-100 text-orange-700 border border-orange-300'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                      {formData.pages.includes(id) ? '✓ ' : ''}{label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* SECTION: Visuels */}
          {activeSection === 'media' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image Desktop (160x600)</label>
                  <input type="url" value={formData.images.desktop}
                    onChange={(e) => setFormData({...formData, images: {...formData.images, desktop: e.target.value}})}
                    className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="URL image" />
                  {formData.images.desktop && (
                    <img src={formData.images.desktop} alt="Desktop" className="mt-2 w-24 h-40 object-cover rounded-lg border" />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image Mobile (320x100)</label>
                  <input type="url" value={formData.images.mobile}
                    onChange={(e) => setFormData({...formData, images: {...formData.images, mobile: e.target.value}})}
                    className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="URL image" />
                  {formData.images.mobile && (
                    <img src={formData.images.mobile} alt="Mobile" className="mt-2 w-40 h-14 object-cover rounded-lg border" />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* SECTION: Style */}
          {activeSection === 'style' && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                {['primary', 'secondary', 'text'].map(colorKey => (
                  <div key={colorKey}>
                    <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">{colorKey}</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={formData.colors[colorKey]}
                        onChange={(e) => setFormData({...formData, colors: {...formData.colors, [colorKey]: e.target.value}})}
                        className="w-10 h-10 rounded cursor-pointer" />
                      <input type="text" value={formData.colors[colorKey]}
                        onChange={(e) => setFormData({...formData, colors: {...formData.colors, [colorKey]: e.target.value}})}
                        className="flex-1 px-2 py-1 border rounded text-xs" />
                    </div>
                  </div>
                ))}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Animation</label>
                  <select value={formData.animation}
                    onChange={(e) => setFormData({...formData, animation: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg">
                    <option value="glow">Glow</option>
                    <option value="pulse">Pulse</option>
                    <option value="slide">Slide</option>
                    <option value="fade">Fade</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* SECTION: Géolocalisation */}
          {activeSection === 'geo' && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.geolocation.target_all}
                    onChange={(e) => setFormData({
                      ...formData,
                      geolocation: {...formData.geolocation, target_all: e.target.checked}
                    })}
                    className="w-5 h-5 text-orange-500 rounded" />
                  <span className="font-medium">Ciblage national (toute la France)</span>
                </label>
              </div>
              
              {!formData.geolocation.target_all && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Régions ciblées</label>
                    <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg">
                      {FRENCH_REGIONS.map(region => (
                        <label key={region} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                          <input type="checkbox"
                            checked={formData.geolocation.regions.includes(region)}
                            onChange={(e) => {
                              const regions = e.target.checked
                                ? [...formData.geolocation.regions, region]
                                : formData.geolocation.regions.filter(r => r !== region);
                              setFormData({...formData, geolocation: {...formData.geolocation, regions}});
                            }}
                            className="w-4 h-4 text-orange-500 rounded" />
                          {region}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Villes spécifiques (séparées par virgules)
                    </label>
                    <input type="text"
                      value={formData.geolocation.cities.join(', ')}
                      onChange={(e) => setFormData({
                        ...formData,
                        geolocation: {
                          ...formData.geolocation,
                          cities: e.target.value.split(',').map(c => c.trim()).filter(Boolean)
                        }
                      })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Paris, Lyon, Marseille..." />
                  </div>
                </>
              )}
            </div>
          )}

          {/* SECTION: Contact Annonceur */}
          {activeSection === 'contact' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom annonceur</label>
                  <input type="text" value={formData.advertiser_name}
                    onChange={(e) => setFormData({...formData, advertiser_name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={formData.advertiser_email}
                    onChange={(e) => setFormData({...formData, advertiser_email: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input type="tel" value={formData.advertiser_phone}
                  onChange={(e) => setFormData({...formData, advertiser_phone: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg" />
              </div>
            </div>
          )}
        </form>

        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
            {saving ? 'Enregistrement...' : (isEdit ? 'Mettre à jour' : 'Créer')}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MODAL DEVIS
// ══════════════════════════════════════════════════════════════════════════════
const QuoteModal = ({ quote, pricing, onClose, onSave }) => {
  const isEdit = !!quote?.id;
  const [formData, setFormData] = useState({
    advertiser_name: quote?.advertiser_name || '',
    advertiser_email: quote?.advertiser_email || '',
    advertiser_phone: quote?.advertiser_phone || '',
    advertiser_company: quote?.advertiser_company || '',
    advertiser_address: quote?.advertiser_address || '',
    lines: quote?.lines || [],
    notes: quote?.notes || '',
    validity_days: 30,
  });
  const [saving, setSaving] = useState(false);

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { description: '', page: 'marketplace', duration: 'month', quantity: 1, unit_price_ht: 0, total_ht: 0 }]
    });
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    
    // Auto-fill price from pricing grid
    if (field === 'page' || field === 'duration') {
      const priceItem = pricing?.prices?.find(p => p.page === newLines[index].page && p.duration === newLines[index].duration);
      if (priceItem) {
        newLines[index].unit_price_ht = priceItem.price_ht;
        newLines[index].description = `Publicité ${PAGE_LABELS[newLines[index].page]} - ${DURATION_LABELS[newLines[index].duration]}`;
      }
    }
    
    // Calculate total
    newLines[index].total_ht = newLines[index].quantity * newLines[index].unit_price_ht;
    setFormData({ ...formData, lines: newLines });
  };

  const removeLine = (index) => {
    setFormData({ ...formData, lines: formData.lines.filter((_, i) => i !== index) });
  };

  const subtotal = formData.lines.reduce((sum, l) => sum + (l.total_ht || 0), 0);
  const tva = subtotal * 0.2;
  const total = subtotal + tva;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      if (isEdit) {
        await axios.put(`${API}/ads/quotes/${quote.id}`, { lines: formData.lines, notes: formData.notes }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/ads/quotes/create`, formData, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(isEdit ? 'Devis mis à jour' : 'Devis créé');
      onSave();
      onClose();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">{isEdit ? 'Modifier le devis' : 'Nouveau devis'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Client Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom annonceur *</label>
              <input type="text" required value={formData.advertiser_name}
                onChange={(e) => setFormData({...formData, advertiser_name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input type="email" required value={formData.advertiser_email}
                onChange={(e) => setFormData({...formData, advertiser_email: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Société</label>
              <input type="text" value={formData.advertiser_company}
                onChange={(e) => setFormData({...formData, advertiser_company: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
              <input type="tel" value={formData.advertiser_phone}
                onChange={(e) => setFormData({...formData, advertiser_phone: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg" />
            </div>
          </div>

          {/* Lines */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Lignes du devis</h3>
              <Button type="button" size="sm" onClick={addLine}><Plus size={16} className="mr-1" /> Ajouter</Button>
            </div>
            
            <div className="border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Page</th>
                    <th className="px-3 py-2 text-left">Durée</th>
                    <th className="px-3 py-2 text-center w-20">Qté</th>
                    <th className="px-3 py-2 text-right w-28">Prix HT</th>
                    <th className="px-3 py-2 text-right w-28">Total HT</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.lines.map((line, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-2">
                        <select value={line.page} onChange={(e) => updateLine(i, 'page', e.target.value)}
                          className="w-full px-2 py-1 border rounded">
                          {Object.entries(PAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select value={line.duration} onChange={(e) => updateLine(i, 'duration', e.target.value)}
                          className="w-full px-2 py-1 border rounded">
                          {Object.entries(DURATION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" min="1" value={line.quantity}
                          onChange={(e) => updateLine(i, 'quantity', parseInt(e.target.value) || 1)}
                          className="w-full px-2 py-1 border rounded text-center" />
                      </td>
                      <td className="px-3 py-2 text-right">{line.unit_price_ht?.toFixed(2)} €</td>
                      <td className="px-3 py-2 text-right font-medium">{line.total_ht?.toFixed(2)} €</td>
                      <td className="px-2">
                        <button type="button" onClick={() => removeLine(i)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totaux */}
            <div className="mt-4 flex justify-end">
              <div className="w-64 space-y-2 text-sm">
                <div className="flex justify-between"><span>Sous-total HT</span><span>{subtotal.toFixed(2)} €</span></div>
                <div className="flex justify-between"><span>TVA (20%)</span><span>{tva.toFixed(2)} €</span></div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total TTC</span><span className="text-orange-600">{total.toFixed(2)} €</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg" rows={3} />
          </div>
        </form>

        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// ONGLET CAMPAGNES
// ══════════════════════════════════════════════════════════════════════════════
const CampaignsTab = () => {
  const [ads, setAds] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [adsRes, statsRes] = await Promise.all([
        axios.get(`${API}/ads/admin/list`, { headers }),
        axios.get(`${API}/ads/admin/stats`, { headers }),
      ]);
      setAds(adsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleToggle = async (ad) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/ads/admin/${ad.id}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(ad.active ? 'Désactivée' : 'Activée');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (ad) => {
    if (!confirm(`Supprimer "${ad.name}" ?`)) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/ads/admin/${ad.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Supprimée');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid sm:grid-cols-5 gap-4">
          {[
            { icon: Megaphone, color: 'blue', value: stats.total_campaigns, label: 'Campagnes' },
            { icon: Eye, color: 'green', value: stats.active_campaigns, label: 'Actives' },
            { icon: BarChart3, color: 'purple', value: stats.total_impressions.toLocaleString(), label: 'Impressions' },
            { icon: MousePointer, color: 'orange', value: stats.total_clicks.toLocaleString(), label: 'Clics' },
            { icon: TrendingUp, color: 'teal', value: `${stats.ctr}%`, label: 'CTR' },
          ].map((s, i) => (
            <Card key={i}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2 bg-${s.color}-100 rounded-lg`}><s.icon size={20} className={`text-${s.color}-600`} /></div>
                <div><p className="text-2xl font-bold">{s.value}</p><p className="text-xs text-gray-500">{s.label}</p></div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Campagnes ({ads.length})</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw size={14} className="mr-1" /> Actualiser</Button>
          <Button onClick={() => { setEditingAd(null); setShowModal(true); }} className="bg-orange-500 hover:bg-orange-600">
            <Plus size={16} className="mr-1" /> Nouvelle
          </Button>
        </div>
      </div>

      {/* Liste */}
      <div className="space-y-3">
        {ads.map(ad => (
          <div key={ad.id} className={`border rounded-xl p-4 ${ad.active ? 'bg-white' : 'bg-gray-50'}`}>
            <div className="flex items-start gap-4">
              <div className="w-16 h-24 rounded-lg flex-shrink-0" style={{
                background: ad.colors?.gradient || '#1E3A5F',
                backgroundImage: ad.images?.desktop ? `url(${ad.images.desktop})` : undefined,
                backgroundSize: 'cover',
              }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold truncate">{ad.name}</h3>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${ad.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                    {ad.active ? 'Active' : 'Inactive'}
                  </span>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">P{ad.priority}</span>
                  {!ad.geolocation?.target_all && (
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                      <MapPin size={10} /> Ciblé
                    </span>
                  )}
                </div>
                <p className="text-sm text-orange-600 font-medium">{ad.tagline}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  <span><Eye size={12} className="inline mr-1" />{ad.impressions.toLocaleString()}</span>
                  <span><MousePointer size={12} className="inline mr-1" />{ad.clicks.toLocaleString()}</span>
                  <span><TrendingUp size={12} className="inline mr-1" />{ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : 0}%</span>
                </div>
                <div className="flex gap-1 mt-2">
                  {ad.pages?.map(p => <span key={p} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">{p}</span>)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => handleToggle(ad)} className={`p-2 rounded-lg ${ad.active ? 'hover:bg-yellow-100 text-yellow-600' : 'hover:bg-green-100 text-green-600'}`}>
                  {ad.active ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
                <button onClick={() => { setEditingAd(ad); setShowModal(true); }} className="p-2 hover:bg-blue-100 text-blue-600 rounded-lg">
                  <Edit2 size={18} />
                </button>
                <button onClick={() => handleDelete(ad)} className="p-2 hover:bg-red-100 text-red-600 rounded-lg">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}
        {ads.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Megaphone size={48} className="mx-auto mb-4 opacity-30" />
            <p>Aucune campagne</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showModal && <AdModal ad={editingAd} onClose={() => setShowModal(false)} onSave={fetchData} />}
      </AnimatePresence>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// ONGLET TARIFS
// ══════════════════════════════════════════════════════════════════════════════
const PricingTab = () => {
  const [pricing, setPricing] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editPrices, setEditPrices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/ads/pricing/active`);
      setPricing(res.data);
      setEditPrices(res.data.prices || []);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPricing(); }, []);

  const updatePrice = (page, duration, value) => {
    const newPrices = [...editPrices];
    const idx = newPrices.findIndex(p => p.page === page && p.duration === duration);
    if (idx >= 0) {
      newPrices[idx].price_ht = parseFloat(value) || 0;
      newPrices[idx].price_ttc = newPrices[idx].price_ht * 1.2;
    }
    setEditPrices(newPrices);
  };

  const savePricing = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (pricing.id) {
        await axios.put(`${API}/ads/pricing/${pricing.id}`, { prices: editPrices }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/ads/pricing/create`, { name: 'Grille Standard', prices: editPrices, active: true }, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success('Tarifs enregistrés');
      setEditing(false);
      fetchPricing();
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Grille tarifaire</h2>
          <p className="text-sm text-gray-500">Prix par page et durée (HT)</p>
        </div>
        {!editing ? (
          <Button onClick={() => setEditing(true)}><Edit2 size={16} className="mr-2" /> Modifier</Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setEditing(false); setEditPrices(pricing.prices); }}>Annuler</Button>
            <Button onClick={savePricing} className="bg-green-500 hover:bg-green-600"><Check size={16} className="mr-2" /> Enregistrer</Button>
          </div>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Page</th>
                {Object.entries(DURATION_LABELS).map(([k, v]) => (
                  <th key={k} className="px-4 py-3 text-center font-medium text-gray-700">{v}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(PAGE_LABELS).map(([pageKey, pageLabel]) => (
                <tr key={pageKey} className="border-t">
                  <td className="px-4 py-3 font-medium">{pageLabel}</td>
                  {Object.keys(DURATION_LABELS).map(dur => {
                    const price = editPrices.find(p => p.page === pageKey && p.duration === dur);
                    return (
                      <td key={dur} className="px-4 py-3 text-center">
                        {editing ? (
                          <input type="number" value={price?.price_ht || 0}
                            onChange={(e) => updatePrice(pageKey, dur, e.target.value)}
                            className="w-24 px-2 py-1 border rounded text-center" />
                        ) : (
                          <div>
                            <span className="font-semibold">{price?.price_ht || 0} €</span>
                            <span className="text-xs text-gray-400 block">({price?.price_ttc?.toFixed(2) || 0} € TTC)</span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// ONGLET DEVIS
// ══════════════════════════════════════════════════════════════════════════════
const QuotesTab = () => {
  const [quotes, setQuotes] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const [quotesRes, pricingRes] = await Promise.all([
        axios.get(`${API}/ads/quotes/list`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/ads/pricing/active`),
      ]);
      setQuotes(quotesRes.data);
      setPricing(pricingRes.data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSend = async (quote) => {
    if (!quote.advertiser_email) {
      toast.error('Aucune adresse email pour cet annonceur');
      return;
    }
    if (!confirm(`Envoyer le devis ${quote.quote_number} par email à ${quote.advertiser_email} ?`)) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/ads/quotes/${quote.id}/send-email`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Devis envoyé à ${quote.advertiser_email}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const handleAccept = async (quote) => {
    if (!confirm('Accepter ce devis ? Une facture sera automatiquement générée.')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/ads/quotes/${quote.id}/accept`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Devis accepté, facture créée');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (quote) => {
    if (!confirm('Supprimer ce devis ?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/ads/quotes/${quote.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Supprimé');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Devis ({quotes.length})</h2>
        <Button onClick={() => { setEditingQuote(null); setShowModal(true); }} className="bg-orange-500 hover:bg-orange-600">
          <Plus size={16} className="mr-1" /> Nouveau devis
        </Button>
      </div>

      <div className="space-y-3">
        {quotes.map(q => (
          <Card key={q.id}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-xl"><FileText size={24} className="text-blue-600" /></div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{q.quote_number}</span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[q.status]}`}>
                        {STATUS_LABELS[q.status]}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{q.advertiser_name} {q.advertiser_company && `- ${q.advertiser_company}`}</p>
                    <p className="text-xs text-gray-400">Créé le {new Date(q.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xl font-bold text-orange-600">{q.total_ttc?.toFixed(2)} €</p>
                    <p className="text-xs text-gray-400">TTC</p>
                  </div>
                  <div className="flex gap-1">
                    <a 
                      href={`${API}/ads/quotes/${q.id}/pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-gray-100 text-gray-600 rounded-lg"
                      title="Télécharger PDF"
                    >
                      <Download size={18} />
                    </a>
                    {q.status === 'draft' && (
                      <button onClick={() => handleSend(q)} className="p-2 hover:bg-blue-100 text-blue-600 rounded-lg" title="Envoyer">
                        <Send size={18} />
                      </button>
                    )}
                    {(q.status === 'draft' || q.status === 'sent') && (
                      <button onClick={() => handleAccept(q)} className="p-2 hover:bg-green-100 text-green-600 rounded-lg" title="Accepter">
                        <CheckCircle size={18} />
                      </button>
                    )}
                    <button onClick={() => handleDelete(q)} className="p-2 hover:bg-red-100 text-red-600 rounded-lg" title="Supprimer">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {quotes.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <FileText size={48} className="mx-auto mb-4 opacity-30" />
            <p>Aucun devis</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showModal && <QuoteModal quote={editingQuote} pricing={pricing} onClose={() => setShowModal(false)} onSave={fetchData} />}
      </AnimatePresence>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// ONGLET FACTURES
// ══════════════════════════════════════════════════════════════════════════════
const InvoicesTab = () => {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const [invoicesRes, statsRes] = await Promise.all([
        axios.get(`${API}/ads/invoices/list`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/ads/invoices/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setInvoices(invoicesRes.data);
      setStats(statsRes.data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSend = async (inv) => {
    if (!inv.advertiser_email) {
      toast.error('Aucune adresse email pour cet annonceur');
      return;
    }
    if (!confirm(`Envoyer la facture ${inv.invoice_number} par email à ${inv.advertiser_email} ?`)) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/ads/invoices/${inv.id}/send-email`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Facture envoyée à ${inv.advertiser_email}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const handleMarkPaid = async (inv) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/ads/invoices/${inv.id}/mark-paid`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Facture marquée comme payée');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid sm:grid-cols-4 gap-4">
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-4">
              <p className="text-sm text-emerald-600 font-medium">CA Encaissé</p>
              <p className="text-2xl font-bold text-emerald-700">{stats.total_revenue?.toFixed(2)} €</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <p className="text-sm text-blue-600 font-medium">En attente</p>
              <p className="text-2xl font-bold text-blue-700">{stats.pending_revenue?.toFixed(2)} €</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">Factures payées</p>
              <p className="text-2xl font-bold">{stats.paid?.count || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">En retard</p>
              <p className="text-2xl font-bold text-orange-600">{stats.overdue?.count || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <h2 className="text-lg font-semibold">Factures ({invoices.length})</h2>

      <div className="space-y-3">
        {invoices.map(inv => (
          <Card key={inv.id}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-xl"><Receipt size={24} className="text-green-600" /></div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{inv.invoice_number}</span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[inv.status]}`}>
                        {STATUS_LABELS[inv.status]}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{inv.advertiser_name}</p>
                    <p className="text-xs text-gray-400">
                      {inv.status === 'paid' && inv.payment_date 
                        ? `Payée le ${new Date(inv.payment_date).toLocaleDateString('fr-FR')}`
                        : `Échéance: ${new Date(inv.due_date).toLocaleDateString('fr-FR')}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xl font-bold text-green-600">{inv.total_ttc?.toFixed(2)} €</p>
                    <p className="text-xs text-gray-400">TTC</p>
                  </div>
                  <div className="flex gap-1">
                    <a 
                      href={`${API}/ads/invoices/${inv.id}/pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-gray-100 text-gray-600 rounded-lg"
                      title="Télécharger PDF"
                    >
                      <Download size={18} />
                    </a>
                    {inv.status === 'draft' && (
                      <button onClick={() => handleSend(inv)} className="p-2 hover:bg-blue-100 text-blue-600 rounded-lg" title="Envoyer">
                        <Send size={18} />
                      </button>
                    )}
                    {(inv.status === 'draft' || inv.status === 'sent') && (
                      <button onClick={() => handleMarkPaid(inv)} className="p-2 hover:bg-green-100 text-green-600 rounded-lg" title="Marquer payée">
                        <CheckCircle size={18} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {invoices.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Receipt size={48} className="mx-auto mb-4 opacity-30" />
            <p>Aucune facture</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════
export default function AdminAdsPage() {
  return (
    <div data-testid="admin-ads-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Megaphone className="text-orange-500" />
          Publicités
        </h1>
        <p className="text-gray-500 mt-1">Campagnes, tarification, devis et factures</p>
      </div>

      <Tabs defaultValue="campaigns" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 max-w-xl">
          <TabsTrigger value="campaigns" className="flex items-center gap-2">
            <Megaphone size={16} /> Campagnes
          </TabsTrigger>
          <TabsTrigger value="pricing" className="flex items-center gap-2">
            <Euro size={16} /> Tarifs
          </TabsTrigger>
          <TabsTrigger value="quotes" className="flex items-center gap-2">
            <FileText size={16} /> Devis
          </TabsTrigger>
          <TabsTrigger value="invoices" className="flex items-center gap-2">
            <Receipt size={16} /> Factures
          </TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns"><CampaignsTab /></TabsContent>
        <TabsContent value="pricing"><PricingTab /></TabsContent>
        <TabsContent value="quotes"><QuotesTab /></TabsContent>
        <TabsContent value="invoices"><InvoicesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
